import React, { useState, useEffect, useRef } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import SEO from "components/SEO/SEO";
import { useDispatch, useSelector } from "react-redux";
import {
  MapPin,
  Calendar,
  Star,
  ArrowLeft,
  Check,
  MessageCircle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  X,
  Play,
  Pause,
  Volume2,
  VolumeX,
  Users,
  RotateCcw,
  Clock,
  Building2,
  Utensils,
  Wine,
  Ticket,
  RefreshCw,
  Globe,
  Home,
  Plane,
  Compass,
  Mountain,
  GraduationCap,
  LayoutGrid,
  Lock,
} from "lucide-react";
import BgGlassmorphism from "components/BgGlassmorphism/BgGlassmorphism";
import {
  getExperienceDetail,
  clearExperienceDetail,
} from "../../redux/reducers/Experiences/ExperiencesSlice";
import { AppDispatch, RootState } from "../../redux/store";
import ReactGA from "react-ga4";
import ExperienceBookingModal from "components/ExperienceBookingModal/ExperienceBookingModal";

// ── CONSTANTS ─────────────────────────────────────────────

const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: "$",
  PEN: "S/",
  EUR: "€",
};
const CURRENCY_NAMES: Record<string, string> = {
  USD: "US Dollar",
  PEN: "Peruvian Sol",
  EUR: "Euro",
};

const AUDIENCE_ICONS: Record<string, React.ElementType> = {
  standard: Globe,
  resident: Home,
  international: Plane,
  local: MapPin,
};

const whatsappNumber = "51924634308";

// Adjust these to your real site header height. The section tabs stick just
// below the header; the booking card sticks below header + tabs.
const NAV_TOP = "top-[72px]";
const CARD_TOP = "lg:top-[148px]";
const SECTION_OFFSET = "scroll-mt-40";

// ── TYPES ─────────────────────────────────────────────────

type MediaItem = {
  type: "image" | "video";
  src: string;
  title?: string | null;
};

// ── HELPERS ───────────────────────────────────────────────

const formatPrice = (
  amount: string | null | undefined,
  currency = "USD",
): string => {
  if (!amount) return "TBA";
  const symbol = CURRENCY_SYMBOLS[currency] || "$";
  const num = parseFloat(amount);
  return `${symbol}${num % 1 === 0 ? num.toFixed(0) : num.toFixed(2)}`;
};

const formatDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return "";
  return new Date(dateStr + "T00:00:00").toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

const formatEventDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return "";
  return new Date(dateStr + "T00:00:00").toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

const formatTime = (timeStr: string | null | undefined): string => {
  if (!timeStr) return "";
  const [h, m] = timeStr.split(":");
  const hour = parseInt(h);
  const ampm = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 || 12;
  return `${hour12}:${m} ${ampm}`;
};

// Shows the first frame of a video as its thumbnail (no separate poster needed)
const videoThumb = (src: string) => `${src}#t=0.5`;

// ── MEDIA VIEWER (one carousel for photos AND videos) ─────

const MediaViewer: React.FC<{
  items: MediaItem[];
  startIndex: number;
  title: string;
  onClose: () => void;
}> = ({ items, startIndex, title, onClose }) => {
  const [filter, setFilter] = useState<"all" | "image" | "video">("all");
  const [currentIndex, setCurrentIndex] = useState(startIndex);
  const [muted, setMuted] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [showReplay, setShowReplay] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const videoRef = useRef<HTMLVideoElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const activeThumbRef = useRef<HTMLButtonElement>(null);
  const touchStartX = useRef<number | null>(null);

  const photoCount = items.filter((i) => i.type === "image").length;
  const videoCount = items.length - photoCount;

  const visible = items
    .map((_, i) => i)
    .filter((i) => filter === "all" || items[i].type === filter);
  const pos = Math.max(0, visible.indexOf(currentIndex));
  const current = items[currentIndex];
  const isVideo = current?.type === "video";

  const goTo = (p: number) => {
    if (p < 0 || p >= visible.length) return;
    setCurrentIndex(visible[p]);
  };
  const next = () => goTo(pos + 1);
  const prev = () => goTo(pos - 1);

  const fmt = (secs: number) => {
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  // Lock page scroll, focus the close button, restore on exit
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  // Keyboard: Esc closes, arrows navigate
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, filter]);

  // Reset video state whenever the item changes
  useEffect(() => {
    setShowReplay(false);
    setIsPaused(false);
    setCurrentTime(0);
    setDuration(0);
  }, [currentIndex]);

  // Keep the active thumbnail in view
  useEffect(() => {
    activeThumbRef.current?.scrollIntoView({
      inline: "center",
      block: "nearest",
      behavior: "smooth",
    });
  }, [currentIndex, filter]);

  const changeFilter = (f: "all" | "image" | "video") => {
    setFilter(f);
    const stillVisible = f === "all" || items[currentIndex].type === f;
    if (!stillVisible) {
      const first = items.findIndex((i) => i.type === f);
      if (first >= 0) setCurrentIndex(first);
    }
  };

  const togglePlayPause = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play().catch(() => {});
    else v.pause();
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !videoRef.current.muted;
    setMuted(videoRef.current.muted);
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!videoRef.current || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.max(
      0,
      Math.min(1, (e.clientX - rect.left) / rect.width),
    );
    videoRef.current.currentTime = ratio * duration;
    setCurrentTime(ratio * duration);
  };

  const handleEnded = () => {
    // Auto-advance only into another video; otherwise offer a replay
    const nextItem = items[visible[pos + 1]];
    if (nextItem?.type === "video") next();
    else setShowReplay(true);
  };

  const handleReplay = () => {
    setShowReplay(false);
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play().catch(() => {});
    }
  };

  const segments: { key: "all" | "image" | "video"; label: string }[] = [
    { key: "all", label: `All ${items.length}` },
    ...(photoCount > 0 && videoCount > 0
      ? [
          { key: "image" as const, label: `Photos ${photoCount}` },
          { key: "video" as const, label: `Videos ${videoCount}` },
        ]
      : []),
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-[#030712] text-white"
      role="dialog"
      aria-modal="true"
      aria-label={`${title} gallery`}
    >
      {/* Top bar */}
      <div className="h-16 sm:h-20 px-3 sm:px-8 flex items-center gap-3 sm:gap-6 flex-shrink-0">
        <button
          ref={closeRef}
          onClick={onClose}
          aria-label="Close gallery"
          className="w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors flex-shrink-0"
        >
          <X className="w-5 h-5 sm:w-6 sm:h-6" />
        </button>
        <span className="hidden md:block w-64 truncate text-sm font-semibold text-gray-200">
          {title}
        </span>

        {segments.length > 1 && (
          <div className="mx-auto flex p-1 rounded-full bg-white/10">
            {segments.map((s) => (
              <button
                key={s.key}
                onClick={() => changeFilter(s.key)}
                className={`h-9 sm:h-10 px-3 sm:px-5 rounded-full text-xs sm:text-sm font-bold whitespace-nowrap transition-colors ${
                  filter === s.key
                    ? "bg-white text-gray-900"
                    : "text-gray-200 hover:bg-white/10"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        )}

        <span
          className={`${segments.length > 1 ? "" : "ml-auto"} md:w-64 text-right text-sm font-semibold text-gray-300 flex-shrink-0`}
        >
          {pos + 1} / {visible.length}
        </span>
      </div>

      {/* Stage */}
      <div
        className="relative flex-1 min-h-0 flex items-center justify-center px-4 sm:px-24 py-2"
        onTouchStart={(e) => {
          touchStartX.current = e.touches[0].clientX;
        }}
        onTouchEnd={(e) => {
          if (touchStartX.current === null) return;
          const dx = e.changedTouches[0].clientX - touchStartX.current;
          touchStartX.current = null;
          if (Math.abs(dx) > 50) (dx < 0 ? next : prev)();
        }}
        onClick={(e) => {
          // Click on the dark backdrop (not on media/controls) closes
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <button
          onClick={prev}
          disabled={pos === 0}
          aria-label="Previous"
          className="hidden sm:flex absolute left-6 top-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-25 disabled:cursor-not-allowed items-center justify-center transition-colors"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>

        {current &&
          (isVideo ? (
            <div
              className="relative group rounded-2xl overflow-hidden bg-black"
              style={{ height: "100%", aspectRatio: "9/16", maxWidth: "100%" }}
            >
              <video
                key={current.src}
                ref={videoRef}
                className="w-full h-full object-contain cursor-pointer"
                autoPlay
                playsInline
                muted={muted}
                onEnded={handleEnded}
                onPlay={() => setIsPaused(false)}
                onPause={() => setIsPaused(true)}
                onTimeUpdate={() =>
                  setCurrentTime(videoRef.current?.currentTime ?? 0)
                }
                onLoadedMetadata={() =>
                  setDuration(videoRef.current?.duration ?? 0)
                }
                onClick={togglePlayPause}
              >
                <source src={current.src} type="video/mp4" />
              </video>

              {isPaused && !showReplay && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="w-16 h-16 rounded-full bg-black/50 flex items-center justify-center">
                    <Play className="w-8 h-8 text-white ml-1" />
                  </div>
                </div>
              )}

              {showReplay && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/60">
                  <button
                    onClick={handleReplay}
                    className="flex flex-col items-center gap-2"
                  >
                    <div className="w-16 h-16 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center border-2 border-white/60 transition-colors">
                      <RotateCcw className="w-7 h-7" />
                    </div>
                    <span className="text-sm font-semibold">Replay</span>
                  </button>
                </div>
              )}

              {!showReplay && (
                <div className="absolute bottom-0 left-0 right-0 bg-black/55 backdrop-blur-sm px-3 pt-3 pb-2">
                  <div
                    className="h-1.5 rounded-full bg-white/30 cursor-pointer"
                    onClick={handleProgressClick}
                  >
                    <div
                      className="h-full rounded-full bg-[#f5694b]"
                      style={{
                        width: `${duration ? (currentTime / duration) * 100 : 0}%`,
                      }}
                    />
                  </div>
                  <div className="flex items-center">
                    <button
                      onClick={togglePlayPause}
                      aria-label={isPaused ? "Play" : "Pause"}
                      className="w-11 h-11 flex items-center justify-center"
                    >
                      {isPaused ? (
                        <Play className="w-5 h-5" />
                      ) : (
                        <Pause className="w-5 h-5" />
                      )}
                    </button>
                    <span className="flex-1 text-xs font-semibold tabular-nums select-none">
                      {fmt(currentTime)} / {fmt(duration)}
                    </span>
                    <button
                      onClick={toggleMute}
                      aria-label={muted ? "Unmute" : "Mute"}
                      className="w-11 h-11 flex items-center justify-center"
                    >
                      {muted ? (
                        <VolumeX className="w-5 h-5" />
                      ) : (
                        <Volume2 className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <img
              key={current.src}
              src={current.src}
              alt=""
              draggable={false}
              className="max-h-full max-w-full object-contain rounded-2xl select-none"
            />
          ))}

        <button
          onClick={next}
          disabled={pos === visible.length - 1}
          aria-label="Next"
          className="hidden sm:flex absolute right-6 top-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-25 disabled:cursor-not-allowed items-center justify-center transition-colors"
        >
          <ChevronRight className="w-6 h-6" />
        </button>
      </div>

      {/* Caption */}
      <div className="h-8 px-4 text-center text-sm text-gray-300 truncate flex-shrink-0">
        {current?.title || ""}
      </div>

      {/* Thumbnail strip: photos and videos in one row */}
      <div className="flex-shrink-0 overflow-x-auto scrollbar-hide pb-4 pt-1">
        <div className="flex gap-2 mx-auto w-max px-4">
          {visible.map((i) => {
            const item = items[i];
            const active = i === currentIndex;
            return (
              <button
                key={i}
                ref={active ? activeThumbRef : undefined}
                onClick={() => setCurrentIndex(i)}
                aria-label={`${item.type === "video" ? "Video" : "Photo"} ${i + 1}`}
                aria-current={active}
                className={`relative flex-shrink-0 w-20 h-14 sm:w-[104px] sm:h-[78px] rounded-xl overflow-hidden bg-gray-800 transition-all ${
                  active
                    ? "ring-[3px] ring-[#f5694b] opacity-100"
                    : "opacity-60 hover:opacity-100"
                }`}
              >
                {item.type === "video" ? (
                  <>
                    <video
                      src={videoThumb(item.src)}
                      muted
                      playsInline
                      preload="metadata"
                      className="w-full h-full object-cover pointer-events-none"
                    />
                    <span className="absolute inset-0 flex items-center justify-center">
                      <span className="w-7 h-7 rounded-full bg-white flex items-center justify-center">
                        <Play className="w-3 h-3 text-[#f5694b] ml-0.5" />
                      </span>
                    </span>
                  </>
                ) : (
                  <img
                    src={item.src}
                    alt=""
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ── GALLERY (mosaic on desktop, swipe carousel on mobile) ──

const GalleryTile: React.FC<{
  item: MediaItem;
  index: number;
  onOpen: (i: number) => void;
  className?: string;
  style?: React.CSSProperties;
  eager?: boolean;
}> = ({ item, index, onOpen, className = "", style, eager }) => (
  <button
    onClick={() => onOpen(index)}
    aria-label={item.type === "video" ? "Play video" : "Open photo"}
    className={`group relative overflow-hidden bg-gray-200 dark:bg-neutral-700 ${className}`}
    style={style}
  >
    {item.type === "video" ? (
      <>
        <video
          src={videoThumb(item.src)}
          muted
          playsInline
          preload="metadata"
          className="w-full h-full object-cover pointer-events-none"
        />
        <span className="absolute inset-0 bg-black/20" />
        <span className="absolute inset-0 flex items-center justify-center">
          <span className="w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center group-hover:scale-110 transition-transform">
            <Play className="w-5 h-5 text-[#f5694b] ml-0.5" />
          </span>
        </span>
        <span className="absolute left-3 bottom-3 text-xs font-bold text-white bg-black/60 px-2.5 py-1 rounded-full">
          Video{item.title ? ` · ${item.title}` : ""}
        </span>
      </>
    ) : (
      <img
        src={item.src}
        alt=""
        loading={eager ? "eager" : "lazy"}
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
      />
    )}
  </button>
);

const Gallery: React.FC<{
  items: MediaItem[];
  onOpen: (i: number) => void;
}> = ({ items, onOpen }) => {
  const [slide, setSlide] = useState(0);

  const photoCount = items.filter((i) => i.type === "image").length;
  const videoCount = items.length - photoCount;

  // Mosaic tiles: next 3 photos, then the first video (or a 4th photo)
  const photoIdx = items
    .map((it, i) => (it.type === "image" ? i : -1))
    .filter((i) => i >= 0);
  const videoIdx = items
    .map((it, i) => (it.type === "video" ? i : -1))
    .filter((i) => i >= 0);
  const tileIdx = [
    ...photoIdx.slice(1, 4),
    ...(videoIdx.length > 0 ? [videoIdx[0]] : photoIdx.slice(4, 5)),
  ].slice(0, 4);

  const full = tileIdx.length >= 4;
  const cols = full ? 4 : tileIdx.length > 0 ? 3 : 1;
  const rows = full ? 2 : Math.max(tileIdx.length, 1);

  return (
    <>
      {/* Desktop / tablet mosaic (capped height) */}
      <div className="hidden md:block relative">
        <div
          className="h-[360px] lg:h-[440px] grid gap-2 rounded-3xl overflow-hidden"
          style={{
            gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
            gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))`,
          }}
        >
          <GalleryTile
            item={items[0]}
            index={0}
            onOpen={onOpen}
            eager
            style={{
              gridColumn: cols === 1 ? "span 1" : "span 2",
              gridRow: `span ${rows}`,
            }}
          />
          {tileIdx.map((i) => (
            <GalleryTile key={i} item={items[i]} index={i} onOpen={onOpen} />
          ))}
        </div>

        {items.length > 1 && (
          <button
            onClick={() => onOpen(0)}
            className="absolute right-6 bottom-6 h-11 px-4 rounded-full bg-white text-gray-900 border-2 border-gray-900 text-sm font-bold inline-flex items-center gap-2 shadow hover:bg-gray-100 transition-colors"
          >
            <LayoutGrid className="w-4 h-4" />
            Show all {photoCount} photo{photoCount !== 1 ? "s" : ""}
            {videoCount > 0 &&
              ` · ${videoCount} video${videoCount !== 1 ? "s" : ""}`}
          </button>
        )}
      </div>

      {/* Mobile swipe carousel */}
      <div className="md:hidden relative -mx-4">
        <div
          className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide h-[292px]"
          onScroll={(e) => {
            const el = e.currentTarget;
            setSlide(Math.round(el.scrollLeft / el.clientWidth));
          }}
        >
          {items.map((item, i) => (
            <GalleryTile
              key={i}
              item={item}
              index={i}
              onOpen={onOpen}
              eager={i === 0}
              className="flex-shrink-0 w-full h-full snap-center"
            />
          ))}
        </div>
        {items.length > 1 && (
          <button
            onClick={() => onOpen(slide)}
            className="absolute right-3 bottom-3 h-11 px-4 rounded-full bg-gray-900/85 text-white text-xs font-bold inline-flex items-center gap-2"
          >
            {videoCount > 0 && <Play className="w-3.5 h-3.5 fill-white" />}
            {slide + 1} / {items.length}
            {videoCount > 0 &&
              ` · ${videoCount} video${videoCount !== 1 ? "s" : ""}`}
          </button>
        )}
      </div>
    </>
  );
};

// ── SHARED SECTION WRAPPER ────────────────────────────────

const Section: React.FC<{
  id?: string;
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}> = ({ id, title, action, children }) => (
  <section id={id} className={SECTION_OFFSET}>
    <div className="flex items-center justify-between gap-4 mb-4 sm:mb-5">
      <h2 className="text-xl sm:text-[28px] font-bold tracking-tight text-gray-900 dark:text-white">
        {title}
      </h2>
      {action}
    </div>
    {children}
  </section>
);

// ── SECTION TABS ──────────────────────────────────────────

const SectionTabs: React.FC<{ tabs: { id: string; label: string }[] }> = ({
  tabs,
}) => {
  const [active, setActive] = useState(tabs[0]?.id);
  const key = tabs.map((t) => t.id).join("|");

  useEffect(() => {
    const els = tabs
      .map((t) => document.getElementById(t.id))
      .filter(Boolean) as HTMLElement[];
    if (!els.length || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: "-20% 0px -70% 0px" },
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return (
    <nav
      className={`sticky ${NAV_TOP} z-30 bg-white/95 dark:bg-neutral-900/95 backdrop-blur border-b border-gray-200 dark:border-neutral-800`}
      aria-label="Page sections"
    >
      <div className="container mx-auto px-4">
        <div className="flex gap-2 overflow-x-auto scrollbar-hide py-2">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() =>
                document
                  .getElementById(t.id)
                  ?.scrollIntoView({ behavior: "smooth", block: "start" })
              }
              className={`flex-shrink-0 h-11 px-4 rounded-full text-sm font-semibold transition-colors ${
                active === t.id
                  ? "bg-gray-900 dark:bg-white text-white dark:text-gray-900"
                  : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-neutral-800"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>
    </nav>
  );
};

// ── FORMATTED TEXT (paragraphs, line breaks, bullets, **bold**) ──

const renderInline = (text: string): React.ReactNode[] => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) =>
    part.startsWith("**") && part.endsWith("**") ? (
      <strong key={i}>{part.slice(2, -2)}</strong>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    ),
  );
};

const FormattedText: React.FC<{ text: string; className?: string }> = ({
  text,
  className = "",
}) => {
  if (!text) return null;
  const blocks = text.trim().split(/\n\s*\n/);

  return (
    <div className={className}>
      {blocks.map((block, i) => {
        const lines = block
          .split("\n")
          .map((l) => l.trim())
          .filter(Boolean);
        const isList =
          lines.length > 0 && lines.every((l) => /^[-•*]\s+/.test(l));

        if (isList) {
          return (
            <ul key={i} className="space-y-2 mb-4 last:mb-0">
              {lines.map((line, j) => (
                <li key={j} className="flex items-start gap-2.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#f5694b] mt-2.5 flex-shrink-0" />
                  <span>{renderInline(line.replace(/^[-•*]\s+/, ""))}</span>
                </li>
              ))}
            </ul>
          );
        }

        return (
          <p key={i} className="mb-4 last:mb-0">
            {lines.map((line, j) => (
              <React.Fragment key={j}>
                {renderInline(line)}
                {j < lines.length - 1 && <br />}
              </React.Fragment>
            ))}
          </p>
        );
      })}
    </div>
  );
};

// ── ABOUT (collapses long text) ───────────────────────────

const AboutSection: React.FC<{ title: string; text: string }> = ({
  title,
  text,
}) => {
  const [expanded, setExpanded] = useState(false);
  const isLong = text.length > 450;

  return (
    <Section id="overview" title={title}>
      <div
        className="relative overflow-hidden"
        style={!expanded && isLong ? { maxHeight: 176 } : undefined}
      >
        <FormattedText
          text={text}
          className="text-gray-700 dark:text-gray-300 leading-relaxed text-base sm:text-[17px]"
        />
        {!expanded && isLong && (
          <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-white dark:from-neutral-900 to-transparent pointer-events-none" />
        )}
      </div>
      {isLong && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="mt-2 min-h-[44px] text-[15px] font-bold underline text-gray-900 dark:text-white"
        >
          {expanded ? "Show less" : "Read more"}
        </button>
      )}
    </Section>
  );
};

// ── KEY FACTS STRIP ───────────────────────────────────────

const FactsStrip: React.FC<{ facts: { label: string; value: string }[] }> = ({
  facts,
}) => {
  if (!facts.length) return null;
  return (
    <div className="grid grid-cols-2 gap-2 md:flex md:gap-0 md:border md:border-gray-200 md:dark:border-neutral-700 md:rounded-2xl md:divide-x md:divide-gray-200 md:dark:divide-neutral-700">
      {facts.map((f) => (
        <div
          key={f.label}
          className="border border-gray-200 dark:border-neutral-700 rounded-2xl md:border-0 md:rounded-none px-3.5 py-3 md:px-5 md:py-4 md:flex-1 min-w-0"
        >
          <p className="text-[11px] md:text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
            {f.label}
          </p>
          <p className="mt-0.5 text-[15px] md:text-[17px] font-bold text-gray-900 dark:text-white truncate">
            {f.value}
          </p>
        </div>
      ))}
    </div>
  );
};

// ── TRIP PROFILE ("Is this trip for me?") ─────────────────

const TripProfile: React.FC<{ experience: any }> = ({ experience }) => {
  const items = [
    {
      label: "Travel style",
      value: experience.travel_style,
      detail: experience.travel_style_detail,
      Icon: Compass,
    },
    {
      label: "Accommodation",
      value: experience.accommodation,
      detail: experience.accommodation_detail,
      Icon: Building2,
    },
    {
      label: "Physical rating",
      value: experience.physical_rating,
      detail: experience.physical_rating_detail,
      Icon: Mountain,
    },
    {
      label: "Trip type",
      value: experience.trip_type,
      detail: experience.trip_type_detail,
      Icon: Users,
    },
    {
      label: "Age requirement",
      value: experience.age_requirement,
      detail: experience.age_requirement_detail,
      Icon: GraduationCap,
    },
  ].filter((i) => i.value);

  if (!items.length) return null;

  return (
    <Section title="Is this trip for me?">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        {items.map(({ label, value, detail, Icon }) => (
          <div
            key={label}
            className="flex gap-3.5 p-4 sm:p-[18px] border border-gray-200 dark:border-neutral-700 rounded-2xl"
          >
            <div className="w-10 h-10 flex-shrink-0 rounded-xl bg-[#f5694b]/10 flex items-center justify-center">
              <Icon className="w-5 h-5 text-[#e2583d]" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                {label}
              </p>
              <p className="text-base font-bold text-gray-900 dark:text-white">
                {value}
              </p>
              {detail && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {detail}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
};

// ── EVENT DETAILS ─────────────────────────────────────────

const EventDetails: React.FC<{ experience: any }> = ({ experience }) => {
  const cells = [
    experience.event_date && {
      Icon: Calendar,
      label: "Date",
      value: formatEventDate(experience.event_date),
    },
    experience.start_time && {
      Icon: Clock,
      label: "Time",
      value: `${formatTime(experience.start_time)}${
        experience.end_time ? ` — ${formatTime(experience.end_time)}` : ""
      }`,
    },
    experience.venue_name && {
      Icon: Building2,
      label: "Venue",
      value: experience.venue_name,
      sub: experience.venue_address,
    },
    experience.capacity && {
      Icon: Users,
      label: "Capacity",
      value: `${experience.capacity} spots`,
    },
    experience.is_recurring && {
      Icon: RefreshCw,
      label: "Recurring",
      value: `${experience.recurrence_rule || "Regular"} event`,
      sub: experience.recurrence_end_date
        ? `Until ${formatDate(experience.recurrence_end_date)}`
        : undefined,
    },
  ].filter(Boolean) as {
    Icon: React.ElementType;
    label: string;
    value: string;
    sub?: string;
  }[];

  const hasExtras = experience.food_included || experience.drinks_included;
  if (!cells.length && !hasExtras) return null;

  return (
    <Section id="event" title="Event details">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        {cells.map(({ Icon, label, value, sub }) => (
          <div
            key={label}
            className="flex gap-3.5 p-4 sm:p-[18px] border border-gray-200 dark:border-neutral-700 rounded-2xl"
          >
            <div className="w-10 h-10 flex-shrink-0 rounded-xl bg-[#f5694b]/10 flex items-center justify-center">
              <Icon className="w-5 h-5 text-[#e2583d]" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                {label}
              </p>
              <p className="text-base font-bold text-gray-900 dark:text-white">
                {value}
              </p>
              {sub && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {sub}
                </p>
              )}
            </div>
          </div>
        ))}
        {hasExtras && (
          <div className="sm:col-span-2 flex flex-wrap items-center gap-2 p-4 sm:p-[18px] border border-gray-200 dark:border-neutral-700 rounded-2xl">
            <span className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mr-2">
              Included
            </span>
            {experience.food_included && (
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold bg-gray-100 dark:bg-neutral-700 text-gray-800 dark:text-gray-200 px-3 py-1.5 rounded-full">
                <Utensils className="w-3.5 h-3.5" /> Food
              </span>
            )}
            {experience.drinks_included && (
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold bg-gray-100 dark:bg-neutral-700 text-gray-800 dark:text-gray-200 px-3 py-1.5 rounded-full">
                <Wine className="w-3.5 h-3.5" /> Drinks
              </span>
            )}
          </div>
        )}
      </div>
    </Section>
  );
};

// ── ITINERARY (timeline) ──────────────────────────────────

const Itinerary: React.FC<{
  itinerary: { day: number; title: string; activities: string[] }[];
}> = ({ itinerary }) => {
  const [openDays, setOpenDays] = useState<number[]>(
    itinerary.length ? [itinerary[0].day] : [],
  );
  const allOpen = openDays.length === itinerary.length;
  const toggle = (day: number) =>
    setOpenDays((p) =>
      p.includes(day) ? p.filter((d) => d !== day) : [...p, day],
    );

  return (
    <Section
      id="itinerary"
      title="Itinerary"
      action={
        <button
          onClick={() =>
            setOpenDays(allOpen ? [] : itinerary.map((d) => d.day))
          }
          className="h-11 px-4 rounded-full border border-gray-300 dark:border-neutral-600 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-neutral-800 transition-colors"
        >
          {allOpen ? "Collapse all" : "Expand all"}
        </button>
      }
    >
      <div>
        {itinerary.map((day, idx) => {
          const isOpen = openDays.includes(day.day);
          const isLast = idx === itinerary.length - 1;
          return (
            <div key={day.day} className="flex gap-4 sm:gap-5">
              <div className="flex flex-col items-center w-8 sm:w-10 flex-shrink-0">
                <div
                  className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 border-[#f5694b] flex items-center justify-center text-sm font-bold ${
                    isOpen
                      ? "bg-[#f5694b] text-white"
                      : "bg-white dark:bg-neutral-900 text-gray-900 dark:text-white"
                  }`}
                >
                  {day.day}
                </div>
                {!isLast && (
                  <div className="flex-1 w-0.5 bg-gray-200 dark:bg-neutral-700 mt-1" />
                )}
              </div>
              <div className={`flex-1 min-w-0 ${isLast ? "" : "pb-2"}`}>
                <button
                  onClick={() => toggle(day.day)}
                  aria-expanded={isOpen}
                  className="w-full min-h-[44px] sm:min-h-[52px] flex items-center justify-between gap-3 text-left"
                >
                  <span className="text-base sm:text-lg font-bold text-gray-900 dark:text-white">
                    {day.title}
                  </span>
                  <ChevronDown
                    className={`w-5 h-5 text-gray-500 flex-shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`}
                  />
                </button>
                {isOpen && (
                  <div className="pb-5">
                    {day.activities.length > 0 ? (
                      <ul className="space-y-2">
                        {day.activities.map((a, i) => (
                          <li
                            key={i}
                            className="flex items-start gap-2.5 text-sm sm:text-[15px] text-gray-600 dark:text-gray-400 leading-relaxed"
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-[#f5694b] mt-2 flex-shrink-0" />
                            {a}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-gray-400 italic">
                        Details coming soon.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Section>
  );
};

// ── INCLUSIONS ────────────────────────────────────────────

const IncludedSection: React.FC<{
  included: string[];
  notIncluded: string[];
}> = ({ included, notIncluded }) => {
  if (!included.length && !notIncluded.length) return null;
  return (
    <Section id="included" title="What's included">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        {included.length > 0 && (
          <div className="p-5 sm:p-6 bg-gray-50 dark:bg-neutral-800 rounded-2xl sm:rounded-[20px]">
            <h3 className="flex items-center gap-2 text-base font-bold text-gray-900 dark:text-white mb-3">
              <Check className="w-5 h-5 text-green-700" /> Included
            </h3>
            <ul className="space-y-2.5">
              {included.map((item, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2.5 text-sm sm:text-[15px] text-gray-700 dark:text-gray-300"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-2 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        )}
        {notIncluded.length > 0 && (
          <div className="p-5 sm:p-6 bg-gray-50 dark:bg-neutral-800 rounded-2xl sm:rounded-[20px]">
            <h3 className="flex items-center gap-2 text-base font-bold text-gray-900 dark:text-white mb-3">
              <X className="w-5 h-5 text-gray-500" /> Not included
            </h3>
            <ul className="space-y-2.5">
              {notIncluded.map((item, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2.5 text-sm sm:text-[15px] text-gray-500 dark:text-gray-400"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-300 mt-2 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Section>
  );
};

// ── PRICING TABLE ─────────────────────────────────────────

const PricingTable: React.FC<{ prices: any[]; currency: string }> = ({
  prices,
  currency,
}) => {
  if (!prices?.length) return null;
  const hasAnyEarlyBird = prices.some((p) => p.has_early_bird);
  const cols = hasAnyEarlyBird ? "1.6fr 1fr 1fr" : "2fr 1fr";

  return (
    <Section id="pricing" title="Pricing by audience">
      <div className="border border-gray-200 dark:border-neutral-700 rounded-2xl sm:rounded-[20px] overflow-hidden">
        <div
          className="grid px-4 sm:px-6 py-3 bg-gray-50 dark:bg-neutral-800 text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400"
          style={{ gridTemplateColumns: cols }}
        >
          <span>Audience</span>
          <span>Regular</span>
          {hasAnyEarlyBird && (
            <span className="text-[#e2583d]">Early bird</span>
          )}
        </div>
        {prices.map((price, i) => {
          const cur = price.currency || currency;
          const Icon = AUDIENCE_ICONS[price.audience_type] || Users;
          return (
            <div
              key={i}
              className="grid items-center px-4 sm:px-6 py-3.5 sm:py-4 border-t border-gray-100 dark:border-neutral-700"
              style={{ gridTemplateColumns: cols }}
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <Icon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white leading-tight truncate">
                    {price.label}
                  </p>
                  <p className="text-[11px] text-gray-400">
                    {CURRENCY_NAMES[cur] || cur}
                  </p>
                </div>
              </div>
              <span
                className={`text-sm sm:text-base ${
                  price.has_early_bird
                    ? "text-gray-400 line-through"
                    : "font-semibold text-gray-900 dark:text-white"
                }`}
              >
                {formatPrice(price.regular_price, cur)}
              </span>
              {hasAnyEarlyBird && (
                <div>
                  {price.has_early_bird ? (
                    <>
                      <span className="text-sm sm:text-base font-bold text-[#e2583d]">
                        {formatPrice(price.early_bird_price, cur)}
                      </span>
                      {price.early_bird_deadline && (
                        <p className="text-[11px] text-gray-400">
                          until {formatDate(price.early_bird_deadline)}
                        </p>
                      )}
                    </>
                  ) : (
                    <span className="text-gray-300">—</span>
                  )}
                </div>
              )}
            </div>
          );
        })}
        <div className="px-4 sm:px-6 py-3 bg-gray-50 dark:bg-neutral-800 text-[13px] text-gray-500 dark:text-gray-400">
          All prices are per person. Your rate is applied automatically when you
          book.
        </div>
      </div>
    </Section>
  );
};

// ── WHAT TO BRING ─────────────────────────────────────────

const WhatToBring: React.FC<{ items: string[] }> = ({ items }) => (
  <Section title="What to bring">
    {items.length > 0 ? (
      <div className="flex flex-wrap gap-2 sm:gap-2.5">
        {items.map((item, i) => (
          <span
            key={i}
            className="inline-flex items-center gap-2 h-10 px-3.5 sm:px-4 rounded-full border border-gray-200 dark:border-neutral-700 text-sm sm:text-[15px] text-gray-700 dark:text-gray-300"
          >
            <Check className="w-3.5 h-3.5 text-[#f5694b]" />
            {item}
          </span>
        ))}
      </div>
    ) : (
      <p className="text-sm text-gray-500">
        Packing list will be shared closer to the trip date.
      </p>
    )}
  </Section>
);

// ── IMPORTANT NOTES ───────────────────────────────────────

const ImportantNotes: React.FC<{ notes: string[] }> = ({ notes }) => {
  if (!notes.length) return null;
  return (
    <section
      id="notes"
      className={`${SECTION_OFFSET} p-5 sm:p-7 bg-[#f5694b]/10 rounded-2xl sm:rounded-[20px]`}
    >
      <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mb-3">
        Important notes
      </h2>
      <ul className="space-y-2.5">
        {notes.map((note, i) => (
          <li
            key={i}
            className="flex items-start gap-2.5 text-sm sm:text-[15px] text-gray-700 dark:text-gray-200 leading-relaxed"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#f5694b] mt-2 flex-shrink-0" />
            {note}
          </li>
        ))}
      </ul>
    </section>
  );
};

// ── UPCOMING DEPARTURES (mobile / tablet section) ─────────

const DeparturesList: React.FC<{ dates: string[] }> = ({ dates }) => {
  if (!dates.length) return null;
  return (
    <div className="lg:hidden">
      <Section title="Upcoming departures">
        <div className="space-y-2">
          {dates.map((date, i) => (
            <div
              key={i}
              className={`h-14 px-4 rounded-2xl flex items-center justify-between border-2 ${
                i === 0
                  ? "border-[#f5694b] bg-[#f5694b]/10"
                  : "border-gray-200 dark:border-neutral-700"
              }`}
            >
              <span className="text-[15px] font-semibold text-gray-900 dark:text-white">
                {date}
              </span>
              <span className="text-xs font-bold text-green-700 dark:text-green-400">
                Available
              </span>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
};

// ── BOOKING CARD (desktop, sticky) ────────────────────────

const BookingCard: React.FC<{
  isEvent: boolean;
  isFree: boolean;
  isPrivate: boolean;
  showBookingButton: boolean;
  buttonLabel: string;
  displayPrice: any;
  displayCurrency: string;
  prices: any[];
  hasEarlyBird: boolean;
  dates: string[];
  onBook: () => void;
  onWhatsApp: () => void;
}> = ({
  isEvent,
  isFree,
  isPrivate,
  showBookingButton,
  buttonLabel,
  displayPrice,
  displayCurrency,
  prices,
  hasEarlyBird,
  dates,
  onBook,
  onWhatsApp,
}) => (
  <div className="p-7 rounded-3xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 shadow-xl space-y-5">
    {/* Price */}
    {isEvent && isFree ? (
      <div>
        <p className="text-4xl font-bold tracking-tight text-green-600">
          Free
        </p>
        <p className="text-sm text-gray-500 mt-1">No ticket required</p>
      </div>
    ) : displayPrice ? (
      <div className="flex items-end justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
            From
          </p>
          <p className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white">
            {CURRENCY_SYMBOLS[displayCurrency] || "$"}
            {parseFloat(displayPrice).toFixed(0)}{" "}
            <span className="text-[15px] font-medium tracking-normal text-gray-500">
              {displayCurrency} / person
            </span>
          </p>
          {prices.length > 1 && (
            <p className="text-xs text-gray-400 mt-1">
              {prices.length} pricing tiers — see the table below
            </p>
          )}
        </div>
        {hasEarlyBird && (
          <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-[#f5694b]/10 text-[#c9452b] flex-shrink-0">
            Early bird
          </span>
        )}
      </div>
    ) : null}

    {/* Departures (trips only) */}
    {!isEvent && dates.length > 0 && (
      <div>
        <p className="text-sm font-bold text-gray-900 dark:text-white mb-2.5">
          Next departures
        </p>
        <div className="space-y-2">
          {dates.slice(0, 3).map((date, i) => (
            <div
              key={i}
              className={`h-12 px-4 rounded-xl flex items-center justify-between border-2 text-[15px] font-semibold text-gray-900 dark:text-white ${
                i === 0
                  ? "border-[#f5694b] bg-[#f5694b]/10"
                  : "border-gray-200 dark:border-neutral-700"
              }`}
            >
              <span>{date}</span>
              <span className="text-xs font-bold text-green-700 dark:text-green-400">
                Available
              </span>
            </div>
          ))}
        </div>
        {dates.length > 3 && (
          <p className="text-sm text-gray-500 mt-2">
            +{dates.length - 3} more departures — ask when you book
          </p>
        )}
      </div>
    )}

    {/* CTA */}
    {showBookingButton ? (
      <div>
        <button
          onClick={onBook}
          className="w-full h-14 rounded-full bg-[#f5694b] hover:bg-[#e2583d] text-white text-[17px] font-bold shadow-lg transition-colors"
        >
          {buttonLabel}
        </button>
        <p className="text-[13px] text-center text-gray-500 mt-2.5">
          {isFree
            ? "Free entry — just let us know you're coming"
            : "No payment required to enquire"}
        </p>
      </div>
    ) : isPrivate ? (
      <div className="h-14 rounded-full bg-gray-100 dark:bg-neutral-700 flex items-center justify-center gap-2 text-sm font-semibold text-gray-600 dark:text-gray-300">
        <Lock className="w-4 h-4" /> Private event — invite only
      </div>
    ) : null}

    <div className="h-px bg-gray-200 dark:bg-neutral-700" />

    <button
      onClick={onWhatsApp}
      className="w-full h-12 rounded-full border-[1.5px] border-gray-900 dark:border-white text-gray-900 dark:text-white text-[15px] font-bold inline-flex items-center justify-center gap-2 hover:bg-gray-900 hover:text-white dark:hover:bg-white dark:hover:text-gray-900 transition-colors"
    >
      <MessageCircle className="w-[18px] h-[18px]" />
      Ask a question on WhatsApp
    </button>
  </div>
);

// ── MAIN PAGE ─────────────────────────────────────────────

const ExperienceDetailPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { experienceDetail: experience, detailLoading } = useSelector(
    (state: RootState) => state.experiences,
  );
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);
  const [bookingOpen, setBookingOpen] = useState(false);

  useEffect(() => {
    if (slug) {
      dispatch(getExperienceDetail(slug));
      ReactGA.event({
        category: "Experience",
        action: "experience_viewed",
        label: slug,
      });
    }
    return () => {
      dispatch(clearExperienceDetail());
    };
  }, [slug, dispatch]);

  useEffect(() => {
    setViewerIndex(null);
  }, [experience?.slug]);

  // Someone who logged in from inside the modal comes back here with
  // ?book=1. Reopen where they left off, then strip the flag — otherwise
  // a refresh or the back button would pop the modal again unprompted.
  useEffect(() => {
    if (!experience) return;
    if (searchParams.get("book") !== "1") return;

    setBookingOpen(true);

    const next = new URLSearchParams(searchParams);
    next.delete("book");
    setSearchParams(next, { replace: true });
  }, [experience, searchParams, setSearchParams]);

  if (detailLoading)
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#f5694b] mx-auto mb-4" />
          <p className="text-sm text-gray-500">Loading experience...</p>
        </div>
      </div>
    );

  if (!experience)
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center">
          <p className="text-sm text-gray-500 mb-4">Experience not found.</p>
          <Link
            to="/experiences"
            className="text-sm text-[#e2583d] hover:underline"
          >
            ← Back to Experiences
          </Link>
        </div>
      </div>
    );

  // ── derived data ──
  const isEvent = experience.is_event ?? false;
  const isFree = experience.is_free ?? false;
  const isPrivate = experience.is_private ?? false;
  const showBookingButton =
    experience.show_booking_button ??
    (!isPrivate && (experience.bookable ?? true));
  const buttonLabel = isFree ? "Register Now" : "Book Now";
  const currency = experience.currency || "USD";
  const fallback =
    "https://images.pexels.com/photos/1271619/pexels-photo-1271619.jpeg";
  const coverImage = experience.cover_image || fallback;
  const galleryImages: string[] =
    experience.images?.length > 0 ? experience.images : [];
  const allImages = [
    coverImage,
    ...galleryImages.filter((img: string) => img !== coverImage),
  ];

  const videos: { url: string; title?: string | null }[] =
    experience.videos && experience.videos.length > 0
      ? experience.videos
      : experience.video_url
        ? [{ url: experience.video_url, title: null }]
        : [];

  // Photos first, then videos — one list for the gallery and the viewer
  const mediaItems: MediaItem[] = [
    ...allImages.map((src: string) => ({ type: "image" as const, src })),
    ...videos.map((v) => ({
      type: "video" as const,
      src: v.url,
      title: v.title,
    })),
  ];

  const prices: any[] =
    experience.prices && experience.prices.length > 0
      ? experience.prices
      : experience.regular_price || experience.early_bird_price
        ? [
            {
              audience_type: "standard",
              label: "Standard",
              currency,
              regular_price: experience.regular_price,
              early_bird_price: experience.early_bird_price,
              early_bird_deadline: experience.early_bird_deadline,
              active_price:
                experience.early_bird_price || experience.regular_price,
              has_early_bird: !!experience.early_bird_price,
            },
          ]
        : [];

  const lowestPrice = prices.reduce((min: any, p: any) => {
    if (!p.active_price) return min;
    if (!min || parseFloat(p.active_price) < parseFloat(min.active_price))
      return p;
    return min;
  }, null);

  const displayPrice =
    lowestPrice?.active_price ||
    experience.early_bird_price ||
    experience.regular_price;
  const displayCurrency = lowestPrice?.currency || currency;

  const nextDates: string[] = experience.next_dates || [];
  const itinerary = experience.itinerary || [];
  const included: string[] = experience.included || [];
  const notIncluded: string[] = experience.not_included || [];
  const importantNotes: string[] = experience.important_notes ?? [];

  const handleBookWhatsApp = () => {
    ReactGA.event({
      category: "Experience",
      action: "whatsapp_booking_clicked",
      label: experience.title,
    });
    const verb = isFree ? "registering for" : "booking";
    const message = encodeURIComponent(
      `Hi! I'm interested in ${verb} "${experience.title}". Could you give me more details?`,
    );
    window.open(`https://wa.me/${whatsappNumber}?text=${message}`, "_blank");
  };

  const openViewer = (index: number) => {
    ReactGA.event({
      category: "Experience",
      action: "gallery_opened",
      label: mediaItems[index]?.type,
    });
    setViewerIndex(index);
  };

  // ── key facts ──
  const facts: { label: string; value: string }[] = (
    isEvent
      ? [
          {
            label: "Date",
            value: experience.event_date
              ? new Date(experience.event_date + "T00:00:00").toLocaleDateString(
                  "en-US",
                  { weekday: "short", month: "short", day: "numeric" },
                )
              : "",
          },
          {
            label: "Time",
            value: experience.start_time
              ? `${formatTime(experience.start_time)}${
                  experience.end_time
                    ? ` — ${formatTime(experience.end_time)}`
                    : ""
                }`
              : "",
          },
          { label: "Venue", value: experience.venue_name || "" },
          {
            label: "Entry",
            value: isFree
              ? "Free"
              : displayPrice
                ? `From ${CURRENCY_SYMBOLS[displayCurrency] || "$"}${parseFloat(displayPrice).toFixed(0)}`
                : "",
          },
        ]
      : [
          { label: "Duration", value: experience.duration || "" },
          { label: "Group size", value: experience.group_size || "" },
          { label: "Difficulty", value: experience.difficulty || "" },
          { label: "Trip type", value: experience.trip_type || "" },
        ]
  ).filter((f) => f.value);

  // ── section tabs (only sections that exist) ──
  const tabs = [
    { id: "overview", label: "Overview" },
    ...(!isEvent && itinerary.length > 0
      ? [{ id: "itinerary", label: "Itinerary" }]
      : []),
    ...(isEvent ? [{ id: "event", label: "Event details" }] : []),
    ...(included.length || notIncluded.length
      ? [{ id: "included", label: "Included" }]
      : []),
    ...(!isFree && prices.length > 0
      ? [{ id: "pricing", label: "Pricing" }]
      : []),
    ...(importantNotes.length > 0 ? [{ id: "notes", label: "Notes" }] : []),
  ];

  return (
    <div className="nc-ExperienceDetailPage pb-36 lg:pb-0">
      <SEO
        title={`${experience.title} — ${isEvent ? "Event" : "Experience"} in Lima, Peru`}
        description={`${(experience.long_description || experience.description || "").slice(0, 155).trim()}...`}
        canonical={`/experiences/${experience.slug}`}
        ogImage={experience.cover_image || undefined}
        ogType="article"
      />
      <BgGlassmorphism />

      {viewerIndex !== null && (
        <MediaViewer
          items={mediaItems}
          startIndex={viewerIndex}
          title={experience.title}
          onClose={() => setViewerIndex(null)}
        />
      )}

      {bookingOpen && (
        <ExperienceBookingModal
          experience={{ ...experience, cover_image: coverImage }}
          isEvent={isEvent}
          isFree={isFree}
          displayPrice={displayPrice}
          displayCurrency={displayCurrency}
          onClose={() => setBookingOpen(false)}
        />
      )}

      {/* Back (desktop row) */}
      <div className="hidden md:block container mx-auto px-4 py-4">
        <Link
          to="/experiences"
          className="inline-flex items-center gap-2 min-h-[44px] text-[15px] font-semibold text-gray-600 dark:text-gray-400 hover:text-[#e2583d] transition-colors"
        >
          <ArrowLeft className="w-[18px] h-[18px]" />
          <span>Back to experiences</span>
        </Link>
      </div>

      {/* ── GALLERY ── */}
      <div className="container mx-auto px-4 relative">
        {/* Back (floating on mobile) */}
        <Link
          to="/experiences"
          aria-label="Back to experiences"
          className="md:hidden absolute left-4 top-3 z-10 w-11 h-11 rounded-full bg-white text-gray-900 shadow flex items-center justify-center"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <Gallery items={mediaItems} onOpen={openViewer} />
      </div>

      {/* ── SECTION TABS ── */}
      <div className="mt-5 sm:mt-6">
        <SectionTabs tabs={tabs} />
      </div>

      {/* ── CONTENT + BOOKING CARD ── */}
      <div className="container mx-auto px-4 mt-8 sm:mt-10 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_400px] gap-10 lg:gap-16 items-start">
        <main className="min-w-0 space-y-10 sm:space-y-14">
          {/* Title block */}
          <section className="space-y-3 sm:space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="px-3 py-1 rounded-full bg-[#f5694b]/10 text-[#c9452b] text-xs sm:text-[13px] font-bold">
                {isEvent ? "Event" : experience.category}
              </span>
              {!isEvent && experience.difficulty && (
                <span className="px-3 py-1 rounded-full bg-gray-100 dark:bg-neutral-700 text-gray-700 dark:text-gray-300 text-xs sm:text-[13px] font-bold">
                  {experience.difficulty}
                </span>
              )}
              {isEvent && isFree && (
                <span className="px-3 py-1 rounded-full bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 text-xs sm:text-[13px] font-bold">
                  FREE
                </span>
              )}
              {isEvent && isPrivate && (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-gray-800 text-white text-xs sm:text-[13px] font-semibold">
                  <Lock className="w-3 h-3" /> Private
                </span>
              )}
              {isEvent && experience.is_recurring && (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-gray-100 dark:bg-neutral-700 text-gray-700 dark:text-gray-300 text-xs sm:text-[13px] font-semibold">
                  <RefreshCw className="w-3 h-3" />
                  {experience.recurrence_rule || "Recurring"}
                </span>
              )}
            </div>

            <h1 className="text-[28px] sm:text-4xl lg:text-[44px] font-bold leading-[1.1] tracking-tight text-gray-900 dark:text-white">
              {experience.title}
            </h1>

            <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm sm:text-base text-gray-600 dark:text-gray-400">
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="w-[18px] h-[18px] text-[#e2583d]" />
                {experience.venue_name || experience.location}
              </span>
              {!isEvent && experience.rating ? (
                <span className="inline-flex items-center gap-1.5">
                  <Star className="w-[18px] h-[18px] fill-[#f5694b] text-[#f5694b]" />
                  <strong className="text-gray-900 dark:text-white">
                    {experience.rating}
                  </strong>
                  ({experience.review_count} reviews)
                </span>
              ) : null}
            </div>

            <div className="pt-1">
              <FactsStrip facts={facts} />
            </div>
          </section>

          {/* Overview */}
          <AboutSection
            title={isEvent ? "About this event" : "About this experience"}
            text={experience.long_description || experience.description || ""}
          />

          {/* Departures (mobile / tablet) */}
          {!isEvent && <DeparturesList dates={nextDates} />}

          {/* Event details or trip profile */}
          {isEvent ? (
            <EventDetails experience={experience} />
          ) : (
            <TripProfile experience={experience} />
          )}

          {/* Itinerary */}
          {!isEvent && itinerary.length > 0 && (
            <Itinerary itinerary={itinerary} />
          )}

          <IncludedSection included={included} notIncluded={notIncluded} />

          {/* Pricing */}
          {!isFree && <PricingTable prices={prices} currency={currency} />}

          {!isEvent && <WhatToBring items={experience.what_to_bring || []} />}

          <ImportantNotes notes={importantNotes} />
        </main>

        {/* Booking card (desktop) */}
        <aside className={`hidden lg:block sticky ${CARD_TOP}`}>
          <BookingCard
            isEvent={isEvent}
            isFree={isFree}
            isPrivate={isPrivate}
            showBookingButton={showBookingButton}
            buttonLabel={buttonLabel}
            displayPrice={displayPrice}
            displayCurrency={displayCurrency}
            prices={prices}
            hasEarlyBird={!!lowestPrice?.has_early_bird}
            dates={nextDates}
            onBook={() => setBookingOpen(true)}
            onWhatsApp={handleBookWhatsApp}
          />
        </aside>
      </div>

      <div className="h-10 lg:h-16" />

      {/* Sticky booking bar (mobile / tablet) */}
      <div className="fixed left-0 right-0 z-40 lg:hidden bottom-[calc(4rem+env(safe-area-inset-bottom))] sm:bottom-0 bg-white dark:bg-neutral-900 border-t border-gray-200 dark:border-neutral-700 px-4 h-[84px] flex items-center gap-3 shadow-[0_-8px_24px_rgba(17,24,39,0.08)]">
        <div className="flex-1 min-w-0">
          {isEvent && isFree ? (
            <>
              <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">
                Entry
              </p>
              <p className="text-[22px] font-bold text-green-600">Free</p>
            </>
          ) : displayPrice ? (
            <>
              <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">
                From
              </p>
              <p className="text-[22px] font-bold text-gray-900 dark:text-white">
                {CURRENCY_SYMBOLS[displayCurrency] || "$"}
                {parseFloat(displayPrice).toFixed(0)}{" "}
                <span className="text-[13px] font-medium text-gray-500">
                  / person
                </span>
              </p>
            </>
          ) : (
            <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
              {experience.title}
            </p>
          )}
        </div>
        {showBookingButton ? (
          <button
            onClick={() => setBookingOpen(true)}
            className="flex-shrink-0 h-[52px] px-7 rounded-full bg-[#f5694b] hover:bg-[#e2583d] text-white text-base font-bold shadow-lg transition-colors"
          >
            {buttonLabel}
          </button>
        ) : isPrivate ? (
          <span className="flex-shrink-0 inline-flex items-center gap-1.5 text-xs font-semibold text-gray-500 px-3">
            <Lock className="w-3.5 h-3.5" /> Invite only
          </span>
        ) : null}
      </div>
    </div>
  );
};

export default ExperienceDetailPage;